import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import {
  PieChart,
  buildPieChartSerie,
} from '@delmonicos/shared-design-system-react/src/components/pie-chart/pie-chart';
import { PieChartLegend } from '@delmonicos/shared-design-system-react/src/components/pie-chart/pie-chart-legend';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { PieChartColors } from '@delmonicos/shared-design-system-react/src/models/enums/pie-chart-colors.enum';
import {
  ThirdPartyEndpointStatisticsModel,
  TransactionCategory,
  TransactionReportsStatisticsByThirdPartyEndpointModel,
} from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/transaction-report/transaction-reports-statistics-by-third-party-endpoint.model';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export enum ByThirdPartyEndpointCardVariantEnum {
  Transactions = 'transactions',
  Revenue = 'revenue',
  Energy = 'energy',
}

interface ByThirdPartyEndpointVariation {
  name: string;
  titleVariant: AccentVariantEnum;
  digits: number;
  unit?: string;
  transformValue?: (value: number) => number;
}

const byThirdPartyEndpointVariations: Record<
  ByThirdPartyEndpointCardVariantEnum,
  ByThirdPartyEndpointVariation
> = {
  [ByThirdPartyEndpointCardVariantEnum.Transactions]: {
    name: 'transactions',
    titleVariant: AccentVariantEnum.Primary,
    digits: 0,
  },
  [ByThirdPartyEndpointCardVariantEnum.Revenue]: {
    name: 'revenue',
    titleVariant: AccentVariantEnum.Accent1,
    digits: 0,
    unit: '€',
  },
  [ByThirdPartyEndpointCardVariantEnum.Energy]: {
    name: 'energy',
    titleVariant: AccentVariantEnum.Secondary,
    digits: 0,
    unit: 'kWh',
  },
};

const categoryOnlyItems = [
  TransactionCategory.FREE_TOKEN,
  TransactionCategory.PAYMENT_TERMINAL_TOKEN,
  TransactionCategory.UNKNOWN,
];

const isCategoryOnlyItem = (item: ThirdPartyEndpointStatisticsModel): boolean =>
  categoryOnlyItems.includes(item.category);

interface ByThirdPartyEndpointCardProps {
  variant?: ByThirdPartyEndpointCardVariantEnum;
  data?: TransactionReportsStatisticsByThirdPartyEndpointModel;
}

export const ByThirdPartyEndpointCard = ({
  data,
  variant = ByThirdPartyEndpointCardVariantEnum.Transactions,
}: ByThirdPartyEndpointCardProps): React.JSX.Element => {
  const { t } = useTranslation();

  const variation = byThirdPartyEndpointVariations[variant];

  const serie = useMemo(() => {
    if (!data?.value) return [];

    return buildPieChartSerie({
      items: data.value,
      getName: (item) =>
        isCategoryOnlyItem(item)
          ? t(
              `delmonicos-shared-sdk.outputs-transaction-category.${item.category}`,
            )
          : (item.afirevName ?? item.thirdPartyEndpointName ?? ''),
      getSubtitle: (item) =>
        !isCategoryOnlyItem(item) && item.afirevName
          ? item.afirevOwnerName
          : undefined,
      getValue: (item) =>
        variation.transformValue
          ? variation.transformValue(item.total ?? 0)
          : (item.total ?? 0),
      getColor: (_item, index) =>
        // @ts-ignore
        PieChartColors[`SERIE_${(index % 4) + 1}`],
      othersLabel: t('$.statistics.cards.by-third-party-endpoint.others'),
      topLimit: 8,
    });
  }, [data, t, variation]);

  return (
    <Card flex={1} gap={GapEnum.M}>
      <AccentTitle
        icon="building-01"
        text={t('$.statistics.cards.by-third-party-endpoint.title')}
        variant={variation.titleVariant}
      />

      <Stack gap={GapEnum.M} flex={1}>
        <PieChart
          serie={serie}
          chartId={`pie-chart-${variation.name}-by-third-party-endpoint`}
        />
        <PieChartLegend
          serie={serie}
          unit={variation.unit}
          digits={variation.digits}
        />
      </Stack>
    </Card>
  );
};
